import { useEffect, useState } from 'react';
import { FloorPlan } from '@components/floorPlan/FloorPlan';
import { Modal } from '@components/modal/Modal';
import { floorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import type { FloorPlanData } from '@shared/types/floorPlans';
import { Icon } from '@components/icon/Icon';

import styles from './FloorPlanCopyModal.scss';

type floorPlanSelectionHandler = (
  fp: FloorPlanData,
  { duplicateTables }: { duplicateTables: boolean },
) => () => Promise<void>;

type Props = {
  floorPlans: FloorPlanData[];
  open: boolean;
  onClose: () => void;
  onOptionClick: floorPlanSelectionHandler;
};

type SelectFloorPlanStepProps = {
  selectedFloorPlan: FloorPlanData;
  onOptionClick: floorPlanSelectionHandler;
};

type SelectCopyOptionsStepProps = {
  floorPlans: FloorPlanData[];
  selectFloorPlanHandler: (fp: FloorPlanData) => void;
};

const SelectFloorPlanStep = ({
  selectedFloorPlan,
  onOptionClick,
}: SelectFloorPlanStepProps) => {
  return (
    <section className={styles.floorPlanCopyModalContentWrapper}>
      <h2>Copy options</h2>
      <p>
        How would you like to copy{' '}
        <span className={styles.selectedFloorPlanName}>
          {selectedFloorPlan.name}
        </span>
        ?
      </p>

      <div className={styles.copyFloorPlanOptionsWrapper}>
        <div
          className={styles.copyFloorPlanOption}
          onClick={onOptionClick(selectedFloorPlan, {
            duplicateTables: true,
          })}
        >
          <div className={styles.iconWrapper}>
            <Icon name="grid" className={styles.withTablesIcon} />
          </div>
          <div>
            <p className={styles.copyFloorPlanOptionTitle}>With Tables</p>
            <p>Copy floor plan including all table placements</p>
          </div>
        </div>
        <div
          className={styles.copyFloorPlanOption}
          onClick={onOptionClick(selectedFloorPlan, {
            duplicateTables: false,
          })}
        >
          <div className={styles.iconWrapper}>
            <Icon name="copy" className={styles.copyIcon} />
          </div>
          <div>
            <p className={styles.copyFloorPlanOptionTitle}>Without Tables</p>
            <p>Copy only the floor plan layout, no tables</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const SelectCopyOptionsStep = ({
  floorPlans,
  selectFloorPlanHandler,
}: SelectCopyOptionsStepProps) => {
  return (
    <section className={styles.floorPlanCopyModalContentWrapper}>
      <h2>Select a floor plan to copy</h2>
      <p>
        Choose which floor plan you want to use as a template for the new one.
      </p>
      <ul className={styles.floorPlansGrid}>
        {floorPlans.map((fp) => (
          <li
            key={`${fp.id}-select-copy-options-step`}
            className={styles.floorPlanPreviewWrapper}
          >
            <button
              onClick={() => selectFloorPlanHandler(fp)}
              className={styles.floorPlanPreviewContent}
            >
              <FloorPlan
                className={styles.floorPlanPreview}
                disableControls
                backgroundSrc={fp.backgroundSrc}
                floorPlanTablesRenderer={floorPlanTablesRendererFactory({
                  tables: fp.floorPlanTables,
                })}
              />
              <p>{fp.name}</p>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export const FloorPlanCopyModal = ({
  floorPlans,
  open,
  onClose,
  onOptionClick,
}: Props) => {
  const [selectedFloorPlan, setSelectedFloorPlan] =
    useState<FloorPlanData | null>(null);

  useEffect(() => {
    if (floorPlans && floorPlans.length === 1) {
      setSelectedFloorPlan(floorPlans[0]);
    }
  }, [floorPlans]);

  const handleOnCloseModal = () => {
    if (floorPlans && floorPlans.length !== 1) {
      setSelectedFloorPlan(null);
    }
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleOnCloseModal}
      ariaLabel="Copy Floor Plan Dialog"
    >
      {selectedFloorPlan ? (
        <SelectFloorPlanStep
          selectedFloorPlan={selectedFloorPlan}
          onOptionClick={onOptionClick}
        />
      ) : (
        <SelectCopyOptionsStep
          floorPlans={floorPlans}
          selectFloorPlanHandler={setSelectedFloorPlan}
        />
      )}
    </Modal>
  );
};
